import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { Skeleton } from "primereact/skeleton";
import { SavedSearchState } from "./SearchFilterContext";
import { useCallback, useEffect, useState } from "react";
import { Fetch } from "../../../shared/Fetch";

interface SavedSearchItem {
  id: number;
  search_Name: string;
  created_Date: string;
  search_Type: "semantic" | "boolean" | string;
  search_State_Json: string | null;
}
// Recursively lowercases the first letter of every object key, at any depth,
// so it doesn't matter whether the backend sent Id/Name/Importance or id/name/importance.
const toCamelDeep = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(toCamelDeep);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), toCamelDeep(v)]));
  }
  return value;
};

const EMPTY_FALLBACK = {
  jobTitleEntries: [],
  skillEntries: [],
} as unknown as SavedSearchState;

const parseState = (json: string | null): SavedSearchState => {
  if (!json) return EMPTY_FALLBACK;

  try {
    const parsed = toCamelDeep(JSON.parse(json));

    return {
      searchType: parsed?.searchType ?? "semantic",
      jobTitleEntries: Array.isArray(parsed?.jobTitleEntries) ? parsed.jobTitleEntries : [],
      skillEntries: Array.isArray(parsed?.skillEntries) ? parsed.skillEntries : [],
      location: Array.isArray(parsed?.location) ? parsed.location : [],
      radius: parsed?.radius ?? "30",
      booleanEntries: parsed?.booleanEntries ?? { name: "", importance: "Required" },
      filters: parsed?.filters ?? {},
    } as SavedSearchState;
  } catch {
    return EMPTY_FALLBACK;
  }
};

const timeAgo = (dateStr: string) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const MonsterSavedSearchesPanel = ({ onRestore, toast }: { onRestore: (state: SavedSearchState) => void; toast: any }) => {
  const [activeIndex, setActiveIndex] = useState(0); // 0 = saved, 1 = archived
  const [items, setItems] = useState<SavedSearchItem[]>([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const tab = activeIndex === 0 ? "saved" : "archived";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Fetch("Monster/GetSemanticSearchHistory", "GET", null, `archived=${tab === "archived"}`, toast);
      if (res) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      }
    } finally {
      setLoading(false);
    }
  }, [tab, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleArchive = async (id: number) => {
    await Fetch(`Monster/ArchiveSearch/${id}`, "PUT", null, "", toast);
    toast.current?.show({ severity: "success", detail: tab === "saved" ? "Search archived" : "Search restored", life: 2000 });
    load();
  };

  const remove = async (id: number) => {
    await Fetch(`Monster/DeleteSearch/${id}`, "DELETE", null, "", toast);
    toast.current?.show({ severity: "success", detail: "Search deleted", life: 2000 });
    load();
  };

  const filtered = items.filter((i) => (i.search_Name ?? "").toLowerCase().includes(filterText.toLowerCase()));

  const renderSkeletons = () => (
    <div className="grid">
      {[1, 2, 3].map((n) => (
        <div className="col-12 md:col-6 lg:col-4" key={n}>
          <div style={{ border: "1px solid #ede9fe", borderRadius: "10px", padding: "18px" }}>
            <Skeleton width="70%" height="18px" style={{ marginBottom: "10px" }} />
            <Skeleton width="40%" height="12px" style={{ marginBottom: "16px" }} />
            <Skeleton width="100%" height="24px" style={{ marginBottom: "8px", borderRadius: "20px" }} />
            <Skeleton width="80%" height="24px" style={{ borderRadius: "20px" }} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderEmptyState = () => (
    <div style={{ textAlign: "center", padding: "56px 24px", color: "#6b7280" }}>
      <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <i className={`pi ${tab === "saved" ? "pi-bookmark" : "pi-inbox"}`} style={{ fontSize: "22px", color: "#7c3aed" }} />
      </div>
      <div style={{ fontSize: "15px", fontWeight: 700, color: "#374151", marginBottom: "4px" }}>{tab === "saved" ? "No saved searches yet" : "No archived searches"}</div>
      <div style={{ fontSize: "13px", color: "#9ca3af" }}>{tab === "saved" ? "Save a search to quickly reuse it later." : "Searches you archive will show up here."}</div>
    </div>
  );

  const renderCards = () => {
    if (loading) return renderSkeletons();
    if (filtered.length === 0) return renderEmptyState();

    return (
      <div className="grid">
        {filtered.map((item) => {
          const state = parseState(item.search_State_Json);
          const isBoolean = item.search_Type === "boolean";
          const criteria = isBoolean ? [state.booleanEntries?.name].filter((c): c is string => !!c?.trim()) : [...state.jobTitleEntries.filter((j) => j.name).map((j) => j.name), ...state.skillEntries.filter((s) => s.name).map((s) => s.name)];
          const isHovered = hoveredId === item.id;

          return (
            <div className="col-12 md:col-6 lg:col-4" key={item.id}>
              <div
                onClick={() => onRestore(state)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  border: `1px solid ${isHovered ? "#c4b5fd" : "#e5e7eb"}`,
                  borderRadius: "10px",
                  padding: "18px 18px 14px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: isHovered ? "0 8px 20px rgba(124, 58, 237, 0.12)" : "0 1px 2px rgba(0,0,0,0.04)",
                  transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                  transition: "all 0.18s ease",
                }}
              >
                {/* ── Header ── */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px", marginBottom: "4px" }}>
                  <div style={{ fontWeight: 700, fontSize: "15px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>{item.search_Name || "Untitled search"}</div>
                  <span style={{ flexShrink: 0, fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", padding: "2px 8px", borderRadius: "20px", backgroundColor: item.search_Type === "semantic" ? "#ede9fe" : "#f0fdf4", color: item.search_Type === "semantic" ? "#6d28d9" : "#15803d" }}>{item.search_Type === "semantic" ? "Semantic" : "Boolean"}</span>
                </div>

                <div style={{ fontSize: "11.5px", color: "#9ca3af", marginBottom: "14px", display: "flex", alignItems: "center", gap: "5px" }}>
                  <i className="pi pi-clock" style={{ fontSize: "10px" }} />
                  {timeAgo(item.created_Date)}
                </div>

                {/* ── Criteria pills ── */}
                {/* ── Criteria: boolean expression as wrapped code text, semantic as pills ── */}
                {isBoolean ? (
                  <div style={{ flex: 1 }}>
                    {criteria.length === 0 ? (
                      <span style={{ fontSize: "12px", color: "#c4c4c4", fontStyle: "italic" }}>No expression set</span>
                    ) : (
                      <div
                        style={{
                          fontSize: "12px",
                          lineHeight: 1.5,
                          color: "#4b5563",
                          backgroundColor: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderLeft: "3px solid #7c3aed",
                          borderRadius: "6px",
                          padding: "8px 10px",
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                          maxHeight: "82px",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                        }}
                      >
                        {criteria[0]}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", flex: 1, alignContent: "flex-start" }}>
                    {criteria.length === 0 && <span style={{ fontSize: "12px", color: "#c4c4c4", fontStyle: "italic" }}>No criteria set</span>}
                    {criteria.slice(0, 5).map((c, i) => (
                      <span key={i} style={{ fontSize: "11.5px", fontWeight: 600, color: "#4b5563", backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "3px 10px", whiteSpace: "nowrap", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c}
                      </span>
                    ))}
                    {criteria.length > 5 && <span style={{ fontSize: "11.5px", fontWeight: 600, color: "#7c3aed", padding: "3px 4px" }}>+{criteria.length - 5} more</span>}
                  </div>
                )}

                {/* ── Footer actions ── */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "2px", marginTop: "14px", paddingTop: "10px", borderTop: "1px solid #f3f4f6" }}>
                  <Button
                    icon="pi pi-search"
                    tooltip="Fill search bar"
                    tooltipOptions={{ position: "top" }}
                    style={{ color: "#7c3aed", width: "34px", height: "34px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestore(state);
                    }}
                  />
                  <Button
                    icon={`pi ${tab === "saved" ? "pi-inbox" : "pi-replay"}`}
                    tooltip={tab === "saved" ? "Archive" : "Unarchive"}
                    tooltipOptions={{ position: "top" }}
                    style={{ width: "34px", height: "34px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleArchive(item.id);
                    }}
                  />
                  <Button
                    icon="pi pi-trash"
                    tooltip="Delete"
                    tooltipOptions={{ position: "top" }}
                    style={{ width: "34px", height: "34px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(item.id);
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
      <style>
        {`
        .monster-saved-tabs .p-tabview-nav { border-bottom: 1px solid #e5e7eb; background: transparent;}
        .monster-saved-tabs .p-tabview-nav li .p-tabview-nav-link {border: none;background: transparent;color: #6b7280;font-weight: 700;font-size: 13px;letter-spacing: 0.03em; padding: 10px 4px;margin-right: 28px;}
        .monster-saved-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {color: #7c3aed; border-bottom: 2.5px solid #7c3aed; box-shadow: none;}
        .monster-saved-tabs .p-tabview-panels {background: transparent;padding: 20px 0 0;}
        .monster-saved-search-input .p-inputtext {border-radius: 20px;padding-left: 34px;}
        `}
      </style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#1f2937", margin: 0 }}>Your searches</h3>

        <span className="p-input-icon-left monster-saved-search-input" style={{ position: "relative" }}>
          <i className="pi pi-search" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "12px", color: "#9ca3af" }} />
          <InputText value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Filter by search title" style={{ width: "260px", fontSize: "13px" }} />
        </span>
      </div>

      <TabView className="monster-saved-tabs" activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="Saved Searches">{renderCards()}</TabPanel>
        <TabPanel header="Archived Searches">{renderCards()}</TabPanel>
      </TabView>
    </div>
  );
};
